import { countryMap } from "../modules/interactions/components/interactionShoppingCart/map/country";

// Per-market Product Detail Page domains (see LIVE-1918).
export const MARKET_AMERICA_DOMAINS: Record<string, string> = {
  usa: "www.marketamerica.com",
  gbr: "www.marketamerica.com",
  idn: "www.marketindonesia.id",
  aus: "marketaustralia.com.au",
  hkg: "markethongkong.com.hk",
  twn: "markettaiwan.com.tw",
  can: "www.marketcanada.ca",
  sgp: "marketsingapore.com.sg",
  mys: "www.marketmalaysia.com.my",
};

export const getMarketAmericaCountry = (locale?: string): string => {
  const regionCode = locale?.split("_")[1]?.toLowerCase();
  if (!regionCode) return "usa";
  return countryMap[regionCode as keyof typeof countryMap] ?? "usa";
};

export const getMarketAmericaDomain = (locale?: string): string =>
  MARKET_AMERICA_DOMAINS[getMarketAmericaCountry(locale)] ?? MARKET_AMERICA_DOMAINS.usa;

export const MARKET_AMERICA_MARKET_NAMES: Record<string, string> = {
  usa: "MARKET AMERICA",
  gbr: "MARKET AMERICA",
  idn: "MARKET INDONESIA",
  aus: "MARKET AUSTRALIA",
  hkg: "MARKET HONG KONG",
  twn: "MARKET TAIWAN",
  can: "MARKET CANADA",
  sgp: "MARKET SINGAPORE",
  mys: "MARKET MALAYSIA",
};

export const getMarketAmericaMarketName = (locale?: string): string =>
  MARKET_AMERICA_MARKET_NAMES[getMarketAmericaCountry(locale)] ?? MARKET_AMERICA_MARKET_NAMES.usa;

// Shop URLs can come back on any per-market domain above (or a subdomain of
// one, e.g. "preview.marketamerica.com") — matching only the bare "usa"
// domain caused non-US products to be misclassified as generic "media"
// interactions instead of "shop_com" (see LIVE-1918 follow-up), which skips
// the in-app product details screen and the attribution URL below entirely.
const MARKET_AMERICA_DOMAIN_TOKENS = Array.from(
  new Set(Object.values(MARKET_AMERICA_DOMAINS).map((d) => d.replace(/^www\./i, "")))
);
const MARKET_AMERICA_URL_PATTERN = new RegExp(
  `(?:${MARKET_AMERICA_DOMAIN_TOKENS.map((d) => d.replace(/\./g, "\\.")).join("|")})\\/?(.*)$`,
  "i"
);

export const isMarketAmericaShopUrl = (url?: string): boolean =>
  !!url && MARKET_AMERICA_URL_PATTERN.test(url);

// publicUserData.locale is frequently unset in live sessions (confirmed via
// debug logging — HK sessions logged locale: undefined), which silently
// defaulted every checkout to the "usa" domain. shopUrl always carries the
// correct per-market domain from the backend, so prefer reading it off there
// and only fall back to locale-based lookup when shopUrl isn't recognized.
export const extractMarketAmericaDomain = (shopUrl?: string): string | null => {
  if (!shopUrl) return null;
  const lower = shopUrl.toLowerCase();
  const match = Object.values(MARKET_AMERICA_DOMAINS).find((domain) =>
    lower.includes(domain.replace(/^www\./i, "").toLowerCase())
  );
  return match ?? null;
};

// Backend shopUrl values are sometimes missing the "/" between the domain and
// the product slug (e.g. "https://www.marketamerica.comultimate-aloe/"
// instead of ".../ultimate-aloe/"). Matching on the domain directly recovers
// the slug regardless of whether that slash is present. The backend has also
// been seen including a leading "products/" segment itself, so it's stripped
// here to avoid double-adding it when the URL is rebuilt.
export const extractMarketAmericaProductSlug = (shopUrl: string): string => {
  const withoutQuery = shopUrl.split("?")[0];
  const match = withoutQuery.match(MARKET_AMERICA_URL_PATTERN);
  const slug = match ? match[1] : withoutQuery.replace(/^https?:\/\/[^/]+\/?/, "");
  return slug.replace(/^\/+|\/+$/g, "").replace(/^products\/+/i, "");
};

export interface MarketAmericaUtmParams {
  source: string;
  medium: string;
  campaign: string;
  term: string;
  distributor: string;
  content: string;
}

export const buildMarketAmericaProductUrl = (
  shopUrl: string,
  thirdPartyUsername: string,
  locale: string | undefined,
  utm: MarketAmericaUtmParams
): string => {
  const slug = extractMarketAmericaProductSlug(shopUrl);
  const domain = extractMarketAmericaDomain(shopUrl) ?? getMarketAmericaDomain(locale);
  const url = new URL(`https://${domain}/${thirdPartyUsername || ""}/products/${slug}/`);
  url.searchParams.set("utm_source", utm.source);
  url.searchParams.set("utm_medium", utm.medium);
  url.searchParams.set("utm_campaign", utm.campaign);
  url.searchParams.set("utm_term", utm.term);
  url.searchParams.set("utm_distributor", utm.distributor);
  url.searchParams.set("utm_content", utm.content);
  return url.toString();
};
